/**
 * TYPES - Tipos para el sistema de tagging semántico
 * 
 * Define las estructuras para gestionar tags con embeddings,
 * permitiendo búsqueda semántica por tags.
 */

// ============================================
// TIPOS DE TAGS
// ============================================

export interface SemanticTag {
    id: string;
    tag: string;                    // Texto del tag (ej: "reconciliación")
    embedding?: number[];           // Vector embedding del tag
    hasEmbedding: boolean;
    conceptId?: string;             // ID del concepto en la ontología (opcional)
    usageCount: number;             // Cuántas veces se usa este tag
    lastUsedAt: string;
    createdAt: string;
}

export interface TagEmbeddingResult {
    tag: string;
    embedding: number[];
    dimensions: number;
}

// ============================================
// DOCUMENTOS CON TAGS
// ============================================

export interface TaggableDocument {
    id: string;
    collectionName: string;
    semanticTags: string[];
    tagsEmbedding?: number[];       // Embedding combinado de todos los tags
    hasTagsEmbedding: boolean;
    ownerId?: string;
}

export interface TagIndexingResult {
    documentId: string;
    success: boolean;
    tagsEmbedding?: number[];
    error?: string;
    processingTime: number;
}

export interface BatchTagIndexingResult {
    successful: number;
    failed: number;
    results: TagIndexingResult[];
    totalTime: number;
}

// ============================================
// BÚSQUEDA POR TAGS
// ============================================

export interface TagSearchOptions {
    /** ID del propietario para filtrar */
    ownerId?: string;
    
    /** Campo del propietario en la colección */
    ownerField?: string;
    
    /** Número máximo de resultados */
    limit?: number;
    
    /** Umbral mínimo de similitud (0-1) */
    similarityThreshold?: number;
}

export interface TagSearchResult<T = unknown> {
    document: T;
    documentId: string;
    tagSimilarity: number;          // Similitud entre query y tags del documento
    matchedTags?: string[];         // Tags que coincidieron semánticamente
}

// ============================================
// CONFIGURACIÓN
// ============================================

export const TAG_EMBEDDING_CONFIG = {
    /** Máximo de tags a combinar para el embedding */
    maxTagsForEmbedding: 10,
    
    /** Umbral de similitud por defecto */
    defaultSimilarityThreshold: 0.35,
    
    /** Máximo de documentos a pre-filtrar por tags */
    maxPreFilterResults: 50,
    
    /** Colecciones soportadas con su campo de propietario */
    supportedCollections: {
        'therapist_note_chunks': 'therapistId',
        'therapist_notes': 'therapistId',
        'school_practices': 'schoolId',
        'user_practice_library': 'userId',
    } as Record<string, string>,
};

